import { Switch } from "@xola/ui-kit";
import { hasOverridableRestriction } from "common";
import React from "react";
import { CatalogItemRestriction } from "types";
import { AddOnLimitOverride } from "@/hooks/useAddOnLimitOverride";

type LimitOverride = Pick<AddOnLimitOverride, "limitError" | "isOverriding"> &
    Partial<Pick<AddOnLimitOverride, "setIsOverriding">>;

interface AddOnLimitOverrideSwitchProps {
    /** Over the limit with no error on this row, such as another attendee over this add-on's limit. */
    isLimitExceeded?: boolean;
    limitOverride: LimitOverride;
    /**
     * Scopes the switch to one add-on row, shown only if one of these limits allows override. The row's control needs a
     * positioned wrapper. Without it the switch overrides every limit on the item.
     */
    restrictions?: CatalogItemRestriction[];
}

/** Stays visible while the override is on, even after the error clears. */
export const AddOnLimitOverrideSwitch = ({
    isLimitExceeded = false,
    limitOverride: { limitError, isOverriding, setIsOverriding },
    restrictions,
}: AddOnLimitOverrideSwitchProps) => {
    if (!limitError && !isOverriding && !isLimitExceeded) {
        return null;
    }

    if (!restrictions) {
        return (
            <div className="mt-2 w-full space-y-1">
                {limitError && <div className="text-red">{limitError}</div>}
                {setIsOverriding && (
                    <div>
                        <Switch.Group>
                            <Switch
                                isChecked={isOverriding}
                                size="small"
                                data-testid="addon-limit-override-toggle"
                                onChange={setIsOverriding}
                            />
                            <Switch.Label className="ml-2">Ignore limits for all add-ons</Switch.Label>
                        </Switch.Group>
                    </div>
                )}
            </div>
        );
    }

    const allowsOverride = hasOverridableRestriction(restrictions);

    // Sits just above the add-on's own control, taking no space, so the rows do not shift as it comes and goes
    return (
        <div
            data-testid="addon-limit-error"
            className="absolute bottom-full right-0 mb-1 flex items-center space-x-2 whitespace-nowrap leading-4"
        >
            {limitError && <span className="text-sm font-bold text-red">{limitError}</span>}
            {setIsOverriding && allowsOverride && (
                <Switch.Group>
                    <Switch
                        isChecked={isOverriding}
                        size="small"
                        data-testid="restriction-override-toggle"
                        onChange={setIsOverriding}
                    />
                    <Switch.Label className="ml-2">Ignore limit</Switch.Label>
                </Switch.Group>
            )}
        </div>
    );
};
